import { useMemo } from 'react';
import { RoutingTabs, useRoutingTabs } from './useRoutingTabs';
import { RoutingTabsInfo } from './routingTabsInfo';
import { colors, Tabs, Typography } from '@sweep-io/sweep-design';
import Stack from '@mui/material/Stack';
import { useRunOnceWhenTruthy } from '../common/useRunOnceWhenTruthy';
import { useSelector } from 'react-redux';
import useTerritories from '../../hooks/useTerritories';
import { selectAssignments } from '../../reducers/global/assignmentReducers';
import { selectScheduledAssignments } from '../../reducers/global/scheduledAssignmentReducers';
import { useAssignmentGroups } from '../pages/assignments/members/useAssignmentGroups';
import { PAGE_SIDE_PADDING } from '../../constants';

export const RoutingTabContent = ({
  goToRoutingHome,
  goToTab,
}: {
  goToRoutingHome: () => void;
  goToTab: (tab: RoutingTabs) => void;
}) => {
  const { routingTab } = useRoutingTabs();
  const ContentComponent = routingTab && RoutingTabsInfo[routingTab].ContentComponent;
  const assignmentRules = useSelector(selectAssignments);
  const scheduledAssignments = useSelector(selectScheduledAssignments);
  const territories = useTerritories();
  const { assignmentGroups, crmOrgUsersBase } = useAssignmentGroups();

  const routingListItems = useMemo(
    () => [
      {
        label: RoutingTabsInfo[RoutingTabs.assignment].label,
        elements: assignmentRules ?? [],
        value: RoutingTabs.assignment,
        badgeLabel: (assignmentRules?.length ?? 0) + '',
      },
      {
        label: RoutingTabsInfo[RoutingTabs.scheduled].label,
        elements: scheduledAssignments ?? [],
        value: RoutingTabs.scheduled,
        badgeLabel: (scheduledAssignments?.length ?? 0) + '',
      },
      {
        label: 'Members',
        elements: crmOrgUsersBase ?? [],
        value: RoutingTabs.members,
        badgeLabel: (crmOrgUsersBase?.length ?? 0) + '',
      },
      {
        label: 'Groups',
        elements: assignmentGroups ?? [],
        value: RoutingTabs.groups,
        badgeLabel: (assignmentGroups?.length ?? 0) + '',
      },
      {
        label: 'Territories',
        elements: territories ?? [],
        value: RoutingTabs.territories,
        badgeLabel: (territories?.length ?? 0) + '',
      },
    ],
    [assignmentRules, scheduledAssignments, crmOrgUsersBase, assignmentGroups, territories],
  );

  useRunOnceWhenTruthy(() => {
    goToTab(RoutingTabs.assignment);
  }, !routingTab);

  return (
    <Stack padding={`0 ${PAGE_SIDE_PADDING}px`} height="100%" gap={3} overflow="hidden">
      <Typography variant="h2">Routing</Typography>

      <Stack
        sx={{
          gap: 3,
          height: '100%',
          '.MuiTabs-root': {
            borderBottom: `1px solid ${colors.grey[120]}`,
          },
          overflow: 'hidden',
        }}
      >
        <Tabs
          tabs={routingListItems}
          value={routingTab}
          onChange={(e, value) => goToTab(value as RoutingTabs)}
        />

        <Stack mt={0} height="100%" overflow="hidden" pb={2} sx={{ mr: -4, ml: -4 }}>
          {!!ContentComponent && <ContentComponent goToRoutingHome={goToRoutingHome} />}
        </Stack>
      </Stack>
    </Stack>
  );
};
